import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DateRangeChooserService } from './date-range-chooser.service';
import { DatePipe } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DateRange, NameDateRange } from './date-range-chooser.model';
import { FORMATS_DASH_NUMBER } from '../utils/format-data-picker';
import { LanguageService } from 'app/core/language.service';
import TimeUtils from '../utils/time-utils';

@Component({
  selector: 'app-date-range-chooser',
  templateUrl: './date-range-chooser.component.html',
  styleUrls: ['./date-range-chooser.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'sv_SE' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS_DASH_NUMBER },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateRangeChooserComponent implements OnInit, OnDestroy {
  public preselectedDate = '';
  @Input() skipLifetime = false;
  /**
   * Renders the trigger with the same metrics as the top-bar dropdowns (.top-bar-dropdown): a shorter
   * box, an 8px radius and 14px text, for pages that sit it next to those controls.
   */
  @Input() compact = false;
  @Input() singleWidgetsDateRange;
  @Input() nameDateRange: string;
  @Input() allTimeStartDate?: Date;
  @Input() allTimeEndDate?: Date;
  rangeFormGroup = new UntypedFormGroup({
    start: new UntypedFormControl(),
    end: new UntypedFormControl(),
  });
  private formSubscription: Subscription;

  constructor(
    public datepipe: DatePipe,
    private _adapter: DateAdapter<any>,
    private languageService: LanguageService,
    @Inject(MAT_DATE_LOCALE) private _locale: string,
    private readonly dateRangeChooserService: DateRangeChooserService,
  ) {}

  ngOnInit(): void {
    this.setLanguage();
    this.formSubscription = this.rangeFormGroup.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((dateRangeInput) => {
        if (!dateRangeInput.start || !dateRangeInput.end) {
          this.dateRangeChooserService.emitDateRange(undefined);
          return;
        }
        const startDate = this.datepipe.transform(dateRangeInput.start, 'yyyy-MM-dd');
        const endDate = this.datepipe.transform(dateRangeInput.end, 'yyyy-MM-dd');
        this.dateRangeChooserService.emitDateRange({
          startDate: startDate,
          endDate: endDate,
          nameDateRange: this.nameDateRange,
        } as DateRange);
      });
    this.preselectedDate = this.singleWidgetsDateRange
      ? ''
      : this.is7daysPreselectedDate
        ? 'dateRange.last7days'
        : 'dateRange.last30days';
    const currentDateRange = this.dateRangeChooserService.dateRangeBS.getValue();

    if (currentDateRange) {
      this.rangeFormGroup.patchValue({
        start: TimeUtils.getDateToDate(currentDateRange.startDate),
        end: TimeUtils.getDateToDate(currentDateRange.endDate),
      });
    } else {
      this.rangeFormGroup.patchValue({
        start: this.singleWidgetsDateRange
          ? TimeUtils.getDateToDate(this.singleWidgetsDateRange.startDate)
          : TimeUtils.getSubtractDate(this.is7daysPreselectedDate ? 7 : 30, 'd').toDate(),
        end: TimeUtils.getDateToDate(),
      });
    }
  }

  public setPreselectedDate(days: number, text: string): void {
    const endDate = TimeUtils.getMoment();
    this.preselectedDate = text;
    if (days === 1) {
      let month = new Date().getMonth();
      let year = new Date().getFullYear();
      if (!month) {
        month = 11;
        year = year - 1;
      } else {
        month = new Date().getMonth() - 1;
      }
      this.rangeFormGroup.patchValue({
        start: TimeUtils.getMoment([year, 0, 1]).month(month).toDate(),
        end: TimeUtils.getMoment([year, 0, 31]).month(month).toDate(),
      });
    } else if (days === 7) {
      const startDate = TimeUtils.getSubtractDate(7, 'days');
      this.rangeFormGroup.patchValue({
        start: startDate.toDate(),
        end: endDate.toDate(),
      });
    } else if (days === 30) {
      const startDate = TimeUtils.getSubtractDate(30, 'days');
      this.rangeFormGroup.patchValue({
        start: startDate.toDate(),
        end: endDate.toDate(),
      });
    } else if (days === 90) {
      const startDate = TimeUtils.getSubtractDate(90, 'days');
      this.rangeFormGroup.patchValue({
        start: startDate.toDate(),
        end: endDate.toDate(),
      });
    } else if (days === 999) {
      this.rangeFormGroup.patchValue({
        start: undefined,
        end: undefined,
      });
    }
  }

  public setAllTime(): void {
    const endDate = this.allTimeEndDate ?? new Date();
    this.preselectedDate = 'dateRange.allTime';
    this.rangeFormGroup.patchValue({
      start: this.allTimeStartDate,
      end: endDate,
    });
  }

  public setLanguage() {
    this._locale = this.languageService.getLocaleFromCurrentLanguage().toString();
    this._adapter.setLocale(this._locale);
  }

  get getComparisonStart() {
    return TimeUtils.getMoment(this.rangeFormGroup.value.start);
  }

  get getComparisonEnd() {
    return TimeUtils.getMoment(this.rangeFormGroup.value.end);
  }

  get is7daysPreselectedDate() {
    return (
      this.nameDateRange === NameDateRange.GeneralPerformance || this.nameDateRange === NameDateRange.VisitorActivity
    );
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
}
